import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { CarIcon, PhoneIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { getEmergencyCallDisplayName } from '@rsos/utils/getEmergencyCallDisplayName';
import useIsFordCall from '../../../../../hooks/useIsFordCall';
import { DateInformation } from './DateInformation';
import { HeaderNotifications } from './HeaderNotifications/HeaderNotifications';
import { LocationInformation } from './LocationInformation';

interface Props {
  callerID: string;
  timestamp: number;
  recentTranscriptionsCount?: number;
  onCallerIdClick?: () => void;
}

const StyledCallerIdMain = styled.span<{ $isClickable?: boolean }>`
  text-decoration: none;
`;

const StyledCollapsedCallerRow = styled(Box)<{ $isClickable?: boolean }>`
  ${({ $isClickable }) =>
    $isClickable
      ? `
    cursor: pointer;
  `
      : ''}
`;

const StyledCollapsedCallerID = styled(Text)<{ $isClickable?: boolean }>`
  display: inline-flex;
  align-items: baseline;
  text-decoration: none;
`;

export const CollapsedHeaderData = ({
  callerID,
  timestamp,
  recentTranscriptionsCount = 0,
  onCallerIdClick,
}: Props) => {
  const isFord = useIsFordCall(callerID);
  const callQueue = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.callQueue,
  );
  const displayName = getEmergencyCallDisplayName(callerID, callQueue);
  const canOpenRecentTranscripts =
    recentTranscriptionsCount > 0 && typeof onCallerIdClick === 'function';

  return (
    <Box>
      <StyledCollapsedCallerRow
        horizontal
        align="center"
        $isClickable={canOpenRecentTranscripts}
        role={canOpenRecentTranscripts ? 'button' : undefined}
        tabIndex={canOpenRecentTranscripts ? 0 : undefined}
        aria-label={
          canOpenRecentTranscripts
            ? `Open recent transcripts (${recentTranscriptionsCount})`
            : undefined
        }
        onClick={canOpenRecentTranscripts ? onCallerIdClick : undefined}
        onKeyDown={
          canOpenRecentTranscripts
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onCallerIdClick?.();
                }
              }
            : undefined
        }
      >
        {isFord ? (
          <CarIcon
            color="panePrimaryText"
            width={16}
            height={16}
            viewboxWidth={24}
            viewboxHeight={24}
          />
        ) : (
          <PhoneIcon
            color="panePrimaryText"
            viewboxHeight={16}
            viewboxWidth={14}
            width={12}
          />
        )}
        <StyledCollapsedCallerID
          $isClickable={canOpenRecentTranscripts}
          color="panePrimaryText"
          fontWeight="bold"
          lineHeight="normal"
          marginLeft={2}
        >
          <StyledCallerIdMain $isClickable={canOpenRecentTranscripts}>
            {displayName}
          </StyledCallerIdMain>
        </StyledCollapsedCallerID>
      </StyledCollapsedCallerRow>
      <DateInformation timestamp={timestamp} />
      <LocationInformation />
      <HeaderNotifications callerID={callerID} />
    </Box>
  );
};
